import React from 'react';
import PropTypes from 'prop-types';
import { LuCalendarClock, LuImage, LuUserRound } from 'react-icons/lu';
import useErrorReportText from '../errorReportText';
import { StatusBadge, StatusSelect, statusMeta } from './reportBits';

const MUTED = 'tw-text-slate-400 dark:tw-text-slate-500';

/** Tooltip of a cell (full description, who changed the status, …). */
export const useCellTitle = () => {
  const { label, formatDate, fieldText } = useErrorReportText();
  return (report, key) => {
    if (key === 'errorSummary') return report.errorSummary || undefined;
    if (key === 'status') {
      return report.statusUpdatedByName
        ? label('table.updatedBy', { name: report.statusUpdatedByName, date: formatDate(report.statusUpdatedAt) })
        : undefined;
    }
    if (key === 'timestamp' || key === 'screenshotUrl') return undefined;
    return fieldText(report, key) || undefined;
  };
};

/** Content of one report table cell. */
const ReportCell = ({ columnKey, report, savingStatus, onStatusChange, onViewScreenshot }) => {
  const { label, formatDate, fieldText } = useErrorReportText();
  const text = (value) => (value ? <span className="tw-block tw-truncate">{value}</span> : <span className={MUTED}>-</span>);

  switch (columnKey) {
    case 'errorSummary': {
      const meta = statusMeta(report.status);
      return (
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
          <span aria-hidden="true" className={`tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full tw-bg-gradient-to-br ${meta.gradient}`} />
          <span className={`tw-min-w-0 tw-truncate tw-font-semibold ${report.errorSummary ? 'tw-text-slate-900 dark:tw-text-white' : MUTED}`}>
            {report.errorSummary || label('table.noSummary')}
          </span>
        </span>
      );
    }
    case 'status':
      return <StatusSelect status={report.status} busy={savingStatus} onChange={(value) => onStatusChange(report.id, value)} />;
    case 'timestamp':
      return text(formatDate(report.timestamp));
    case 'screenshotUrl':
      return report.screenshotUrl ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onViewScreenshot(report.screenshotUrl);
          }}
          onKeyDown={(event) => event.stopPropagation()}
          className="tw-inline-flex tw-h-8 tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-violet-500/10 tw-px-3 tw-text-xs tw-font-semibold tw-text-violet-700 tw-transition-colors hover:tw-bg-violet-500/15 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-violet-500/50 dark:tw-text-violet-300"
        >
          <LuImage aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
          {label('table.view')}
        </button>
      ) : <span className={MUTED}>{label('table.none')}</span>;
    case 'uid':
      return <span className="tw-block tw-truncate tw-font-mono tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{report.uid || '-'}</span>;
    default:
      return text(fieldText(report, columnKey));
  }
};

ReportCell.propTypes = {
  columnKey: PropTypes.string.isRequired,
  report: PropTypes.object.isRequired,
  savingStatus: PropTypes.bool,
  onStatusChange: PropTypes.func.isRequired,
  onViewScreenshot: PropTypes.func.isRequired,
};

/** One row of the card list (narrow screens). */
export const ReportCardRow = ({ report }) => {
  const { label, formatDate } = useErrorReportText();
  const meta = statusMeta(report.status);
  const Icon = meta.icon;
  return (
    <>
      <span aria-hidden="true" className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${meta.gradient}`}>
        <Icon className="tw-h-[18px] tw-w-[18px]" />
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <span className={`tw-line-clamp-2 tw-text-[15px] tw-font-semibold tw-leading-snug ${report.errorSummary ? 'tw-text-slate-900 dark:tw-text-white' : MUTED}`}>
          {report.errorSummary || label('table.noSummary')}
        </span>
        <span className="tw-mt-1 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
          <span className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1">
            <LuUserRound aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
            <span className="tw-truncate">{report.userName}</span>
          </span>
          <span className="tw-inline-flex tw-items-center tw-gap-1">
            <LuCalendarClock aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
            {formatDate(report.timestamp)}
          </span>
        </span>
        <span className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
          <StatusBadge status={report.status} />
          {report.screenshotUrl && (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-violet-500/10 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-violet-700 dark:tw-text-violet-300">
              <LuImage aria-hidden="true" className="tw-h-3 tw-w-3" />
              {label('panel.withScreenshot')}
            </span>
          )}
        </span>
      </span>
    </>
  );
};

ReportCardRow.propTypes = { report: PropTypes.object.isRequired };

export default ReportCell;
